import { FieldArray, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import validation from "./validation";
import Switch from "shared/components/Input/Switch";
import { Divider } from "antd";
import Button from "shared/components/Button";
import {
  MdDeleteOutline,
  MdOutlineAdd,
  MdOutlineContentCopy,
  MdOutlineFileUpload,
} from "react-icons/md";
import Select from "shared/components/Select";
import { INPUT_TYPES_OPTIONS } from "utils/helpers/array";
import { ATTACHMENT_TYPES, INPUT_TYPES_FULL } from "utils/constants";
import { get, isEmpty } from "lodash";
import cuid from "cuid";

const Create = ({ onSubmit, cancel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.TEMPLATE);
  const CREATE = t("CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });

  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("header.logo", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("header.logo", base64);
  };

  return (
    <Formik
      initialValues={{
        name: "",
        active: true,
        theme: {
          colors: [{ label: "", value: "" }],
          fonts: [{ value: "" }],
        },
        form: [],
        header: {
          title: "",
          logo: "",
          actions: [],
        },
      }}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ isValid, handleSubmit, setFieldValue, values, ...formProps }) => [
        <Header
          key={"header"}
          title={CREATE.HEADER.TITLE}
          subtitle={CREATE.HEADER.ACTION}
          actions={[
            {
              text: CREATE.HEADER.DISCARD,
              onClick: cancel,
              type: "primary--outlined",
              icon: <BiChevronLeft size={20} />,
            },
            {
              text: CREATE.HEADER.SAVE,
              onClick: handleSubmit,
              type: "primary",
              icon: <BiCheck size={20} />,
            },
          ]}
        />,
        <Card key="card" type={"simple"}>
          <div className="grid--2">
            <Input
              label={FORM.NAME}
              placeholder={FORM.NAME}
              {...bindInputProps({ name: "name", ...formProps })}
            />

            <div className="d-flex">
              <Switch
                label={FORM.STATUS}
                className="button__switch"
                checkedChildren={FORM.ACTIVE}
                unCheckedChildren={FORM.INACTIVE}
                defaultChecked={true}
                onChange={(checked) => setFieldValue("active", checked)}
              />
            </div>
          </div>
        </Card>,
        <div key="div-1" className="m-top-24" />,
        <Card key="form-card" type={"toggle"} toggle title={FORM.FORMS}>
          <FieldArray
            name="form"
            render={(arrayHelpers) => (
              <div className="m-top-24">
                {values.form.map((form, index) => (
                  <div key={`form-${index}`} className="m-bottom-16">
                    <div className="d-flex justify--end m-bottom-8">
                      <Button
                        type="danger--outlined"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <MdDeleteOutline size={24} />
                      </Button>
                      <Button
                        type="primary--outlined"
                        onClick={() =>
                          arrayHelpers.insert(index + 1, {
                            label: "",
                            name: "",
                            placeholder: "",
                            type: "",
                            required: false,
                            defaultValue: "",
                            options: [],
                          })
                        }
                      >
                        <MdOutlineContentCopy size={24} />
                      </Button>
                    </div>
                    <div className="grid--5 align--start ">
                      <Select
                        label={FORM.TYPE_FIELD}
                        options={INPUT_TYPES_OPTIONS}
                        {...bindArrayInputProps({
                          parent: "form",
                          index: index,
                          name: "type",
                          values,
                          ...formProps,
                        })}
                        onChange={(value) =>
                          setFieldValue(`form[${index}].type`, value)
                        }
                      />
                      <Input
                        label={FORM.LABEL}
                        placeholder={FORM.LABEL}
                        {...bindArrayInputProps({
                          parent: "form",
                          index,
                          name: "label",
                          values,
                          ...formProps,
                        })}
                      />

                      <Input
                        label={FORM.PLACEHOLDER}
                        placeholder={FORM.PLACEHOLDER}
                        {...bindArrayInputProps({
                          parent: "form",
                          index,
                          name: "placeholder",
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.NAME}
                        placeholder={FORM.NAME}
                        {...bindArrayInputProps({
                          parent: "form",
                          index,
                          name: "name",
                          values,
                          ...formProps,
                        })}
                      />
                      {(values.form[index].type === INPUT_TYPES_FULL.CHECKBOX ||
                        values.form[index].type === INPUT_TYPES_FULL.SWITCH ||
                        values.form[index].type ===
                          INPUT_TYPES_FULL.RADIO.SIMPLE ||
                        values.form[index].type ===
                          INPUT_TYPES_FULL.RADIO.GROUP) && (
                        <Switch
                          label={FORM.DEFAULT_VALUE}
                          checkedChildren="Checked"
                          unCheckedChildren="Unchecked"
                          checked={values.form[index].defaultValue}
                          onChange={(checked) =>
                            setFieldValue(
                              `form[${index}].defaultValue`,
                              checked,
                            )
                          }
                        />
                      )}

                      {(values.form[index].type ===
                        INPUT_TYPES_FULL.SELECT.NORMAL ||
                        values.form[index].type ===
                          INPUT_TYPES_FULL.SELECT.ASYNC) && (
                        <FieldArray
                          name={`form[${index}].options`}
                          render={(arrayHelpers) => (
                            <div className="">
                              {(values.form[index]?.options || []).map(
                                (option, optIndex) => (
                                  <div key={optIndex} className="grid--1">
                                    <Input
                                      label={FORM.LABEL}
                                      placeholder="Option Label"
                                      value={option.label}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `form[${index}].options[${optIndex}].label`,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <Input
                                      label={FORM.LABEL}
                                      placeholder="Option Value"
                                      value={option.value}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `form[${index}].options[${optIndex}].value`,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <Button
                                      type="danger--outlined"
                                      onClick={() =>
                                        arrayHelpers.remove(optIndex)
                                      }
                                    >
                                      <MdDeleteOutline size={20} />
                                    </Button>
                                  </div>
                                ),
                              )}
                              <Button
                                type="primary"
                                onClick={() =>
                                  arrayHelpers.push({ label: "", value: "" })
                                }
                              >
                                <MdOutlineAdd size={20} />
                              </Button>
                            </div>
                          )}
                        />
                      )}

                      {values.form[index].type !== INPUT_TYPES_FULL.CHECKBOX &&
                        values.form[index].type !== INPUT_TYPES_FULL.SWITCH &&
                        values.form[index].type !==
                          INPUT_TYPES_FULL.RADIO.SIMPLE &&
                        values.form[index].type !==
                          INPUT_TYPES_FULL.RADIO.GROUP &&
                        values.form[index].type !==
                          INPUT_TYPES_FULL.SELECT.NORMAL &&
                        values.form[index].type !==
                          INPUT_TYPES_FULL.SELECT.ASYNC && (
                          <Input
                            label={FORM.DEFAULT_VALUE}
                            placeholder={FORM.DEFAULT_VALUE}
                            {...bindArrayInputProps({
                              parent: "form",
                              index,
                              name: "defaultValue",
                              values,
                              ...formProps,
                            })}
                          />
                        )}
                      <Switch
                        label={FORM.REQUIRE}
                        className="button__switch"
                        checkedChildren={FORM.REQUIRED}
                        unCheckedChildren={FORM.OPTIONAL}
                        defaultChecked={false}
                        onChange={(checked) =>
                          setFieldValue(`form[${index}].required`, checked)
                        }
                      />
                    </div>
                  </div>
                ))}
                <Divider />
                <div className="p-top-16">
                  <Button
                    type="primary"
                    onClick={() =>
                      arrayHelpers.push({
                        label: "",
                        name: "",
                        placeholder: "",
                        type: "",
                        required: false,
                        defaultValue: "",
                        options: [],
                      })
                    }
                  >
                    <MdOutlineAdd size={24} />
                    {FORM.ADD_NEW}
                  </Button>
                </div>
              </div>
            )}
          />
        </Card>,
        <div key="div-2" className="m-top-24" />,
        <Card
          key="theme-card"
          type={"toggle"}
          toggle={false}
          title={FORM.THEME}
        >
          <FieldArray
            name="theme.colors"
            render={(arrayHelpers) => (
              <div className="m-top-24">
                {values.theme.colors.map((color, index) => (
                  <div key={`color-${index}`} className="m-bottom-16">
                    <div className="d-flex justify--end m-bottom-8">
                      <Button
                        type="danger--outlined"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <MdDeleteOutline size={24} />
                      </Button>
                      <Button
                        type="primary--outlined"
                        onClick={() =>
                          arrayHelpers.insert(index + 1, {
                            label: "",
                            value: "",
                          })
                        }
                      >
                        <MdOutlineContentCopy size={24} />
                      </Button>
                    </div>
                    <div className="grid--2">
                      <Input
                        label={FORM.LABEL}
                        placeholder={FORM.LABEL}
                        {...bindArrayInputProps({
                          parent: "theme.colors",
                          index,
                          name: "label",
                          values,
                          ...formProps,
                        })}
                      />

                      <Input
                        type={"colorPicker"}
                        label={FORM.COLOR}
                        value={color.value}
                        defaultValue={"#0e93e5"}
                        showText={(color) => color.toHexString()}
                        {...bindArrayInputProps({
                          parent: "theme.colors",
                          index,
                          name: "value",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <Divider />
                  </div>
                ))}
                <div className="p-top-8">
                  <Button
                    type="primary"
                    onClick={() => arrayHelpers.push({ label: "", value: "" })}
                  >
                    <MdOutlineAdd size={24} />
                    {FORM.ADD_NEW}
                  </Button>
                </div>
              </div>
            )}
          />
          <div key="div-1" className="m-top-24" />,
          <Divider />
          <h3>Fonts</h3>
          <FieldArray
            name="theme.fonts"
            render={(arrayHelpers) => (
              <div className="m-top-24">
                {values.theme.fonts.map((font, index) => (
                  <div key={`font-${index}`} className="m-bottom-16">
                    <div className="d-flex justify--end m-bottom-8">
                      <Button
                        type="danger--outlined"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <MdDeleteOutline size={24} />
                      </Button>
                      <Button
                        type="primary--outlined"
                        onClick={() =>
                          arrayHelpers.insert(index + 1, { value: "" })
                        }
                      >
                        <MdOutlineContentCopy size={24} />
                      </Button>
                    </div>
                    <div className="grid--1">
                      <Input
                        label={FORM.FONT_NAME}
                        placeholder={FORM.FONT_NAME}
                        {...bindArrayInputProps({
                          parent: "theme.fonts",
                          index,
                          name: "value",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  </div>
                ))}
                <div className="p-top-8">
                  <Button
                    type="primary"
                    onClick={() => arrayHelpers.push({ value: "" })}
                  >
                    <MdOutlineAdd size={24} />
                    {FORM.ADD_NEW}
                  </Button>
                </div>
              </div>
            )}
          />
        </Card>,
        <div key="div-3" className="m-top-24" />,
        <Card
          key="header-card"
          type={"toggle"}
          toggle={false}
          title={FORM.HEADER}
        >
          <div className="grid--2 m-top-24">
            <Input
              label={FORM.HEADER_TITLE}
              placeholder={FORM.HEADER_TITLE}
              value={values.header.title}
              onChange={(e) => setFieldValue("header.title", e.target.value)}
            />

            <Input
              label="Logo"
              type="file"
              variant="uploader"
              listType="picture"
              showUploadList={false}
              maxCount={1}
              onUpload={([file]) => handleFileUpload(file, setFieldValue)}
              fileList={
                isEmpty(get(values, "header.logo", ""))
                  ? []
                  : [get(values, "header.logo")]
              }
            >
              <Button type="primary--link" icon={<MdOutlineFileUpload />}>
                {FORM.UPLOAD}
              </Button>
              <div>
                {get(values, "header.logo") && (
                  <img
                    className="icon"
                    alt="logo"
                    src={get(values, "header.logo")}
                  />
                )}
              </div>
            </Input>
          </div>
          <FieldArray
            name="header.actions"
            render={(arrayHelpers) => (
              <div className="m-top-24">
                <Divider />

                {values.header.actions.map((action, index) => (
                  <div key={`action-${index}`} className="m-bottom-16">
                    <div className="d-flex justify--end m-bottom-8">
                      <Button
                        type="danger--outlined"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <MdDeleteOutline size={24} />
                      </Button>
                      <Button
                        type="primary--outlined"
                        onClick={() =>
                          arrayHelpers.insert(index + 1, { label: "", to: "" })
                        }
                      >
                        <MdOutlineContentCopy size={24} />
                      </Button>
                    </div>
                    <div className="grid--2">
                      <Input
                        title={FORM.NAME}
                        placeholder={FORM.NAME}
                        value={action.label}
                        onChange={(e) =>
                          setFieldValue(
                            `header.actions.${index}.label`,
                            e.target.value,
                          )
                        }
                      />
                      <Input
                        label={FORM.LINK}
                        placeholder={FORM.LINK}
                        value={action.to}
                        onChange={(e) =>
                          setFieldValue(
                            `header.actions.${index}.to`,
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
                <Divider />
                <div className="p-top-16">
                  <Button
                    type="primary"
                    onClick={() => arrayHelpers.push({ label: "", to: "" })}
                  >
                    <MdOutlineAdd size={24} />
                    {FORM.ADD_NEW}
                  </Button>
                </div>
              </div>
            )}
          />
        </Card>,
      ]}
    </Formik>
  );
};

export default Create;
